import { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Menu,
    MenuItem,
    IconButton,
    Tooltip,
    AppBar,
    Toolbar,
    Typography,
    InputBase,
    Divider,
    ListItemText
} from '@mui/material';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';

const MIN_ROWS = 30;
const MIN_COLS = 15;

const INITIAL_DATA = [
    ["Client Code", "Client Name", "Current Outstanding", "Total Invoice", "", "Validate", "Correctness", ""],
    ["CL001", "Acme Corp", 5000, 15000, "", ""],
    ["CL002", "Beta Ltd", 3000, 12000, "", ""],
    ["CL003", "Gamma Inc", 7000, 20000, "", ""],
    ["CL004", "Delta LLC", 2000, 8000, "", ""],
    ["CL005", "Epsilon PLC", 4000, 10000, "", ""]
];

const READONLY_CELL = ["A0:A5"];

const ACCEPTANCE_COLUMN = { F: ["Accept", "Reject"], G: ["Yes", "No"] };

// const ALTERNATE_INITIAL_DATA = [
//     { "Item": "Laptop", "Quantity": 15, "Unit Price": 1200, "Total": "=B2*C2", "Date": "2023-10-01" },
//     { "Item": "Mouse", "Quantity": 50, "Unit Price": 25, "Total": "=B3*C3", "Date": "2023-10-02" },
//     { "Item": "Keyboard", "Quantity": 30, "Unit Price": 45, "Total": "=B4*C4", "Date": "2023-10-03" },
//     { "Item": "Monitor", "Quantity": 20, "Unit Price": 300, "Total": "=B5*C5", "Date": "2023-10-04" },
//     { "Item": "TOTAL", "Quantity": "=SUM(B2:B5)", "Unit Price": "", "Total": "=SUM(D2:D5)", "Date": "" }
// ];

const getColLetter = (index) => {
    let letter = '';
    let temp = index;
    while (temp >= 0) {
        letter = String.fromCharCode((temp % 26) + 65) + letter;
        temp = Math.floor(temp / 26) - 1;
    }
    return letter;
};

const parseCellId = (id) => {
    const match = id.toUpperCase().match(/^([A-Z]+)([0-9]+)$/);
    if (!match) return null;
    let c = 0;
    for (let i = 0; i < match[1].length; i++) c = c * 26 + (match[1].charCodeAt(i) - 64);
    return { r: parseInt(match[2], 10) - 1, c: c - 1 };
};

const parseReadOnlyCellId = (id) => {
    const match = id.toUpperCase().match(/^([A-Z]+)([0-9]+)$/);
    if (!match) return null;
    const position = parseCellId(`${match[1]}${Math.max(1, Number(match[2]))}`);
    return position ? { ...position, r: Math.max(0, Number(match[2]) - 1) } : null;
};

const isReadOnlyCell = (r, c) => READONLY_CELL.some((reference) => {
    const parts = reference.toUpperCase().split(':');
    if (parts.length === 1) {
        if (/^[A-Z]+$/.test(parts[0])) return parseCellId(`${parts[0]}1`)?.c === c;
        const cell = parseReadOnlyCellId(parts[0]);
        return cell?.r === r && cell?.c === c;
    }

    if (parts.length !== 2) return false;
    const start = parseReadOnlyCellId(parts[0]);
    const end = parseReadOnlyCellId(parts[1]);
    if (start && end) {
        return r >= Math.min(start.r, end.r) && r <= Math.max(start.r, end.r) &&
            c >= Math.min(start.c, end.c) && c <= Math.max(start.c, end.c);
    }

    if (/^[A-Z]+$/.test(parts[0]) && /^[A-Z]+$/.test(parts[1])) {
        const startColumn = parseCellId(`${parts[0]}1`).c;
        const endColumn = parseCellId(`${parts[1]}1`).c;
        return c >= Math.min(startColumn, endColumn) && c <= Math.max(startColumn, endColumn);
    }

    return false;
});

const getCellId = (r, c) => `${getColLetter(c)}${r + 1}`;

const getRangeCells = (startId, endId) => {
    const start = parseCellId(startId);
    const end = parseCellId(endId);
    if (!start || !end) return [];
    const cells = [];
    for (let r = Math.min(start.r, end.r); r <= Math.max(start.r, end.r); r++) {
        for (let c = Math.min(start.c, end.c); c <= Math.max(start.c, end.c); c++) {
            cells.push(getCellId(r, c));
        }
    }
    return cells;
};

const isNumericOrDate = (val) => {
    if (val === '' || val == null) return false;
    if (!isNaN(Number(val)) && val.toString().trim() !== '') return true;
    if (!isNaN(Date.parse(val)) && val.toString().match(/[-/]/)) return true;
    return false;
};

export default function SpaxExcel() {
    const generateInitialGrid = () => {
        const rows = Math.max(MIN_ROWS, INITIAL_DATA.length + 5);
        const cols = Math.max(MIN_COLS, Object.keys(INITIAL_DATA[0] || {}).length + 2);

        const newGrid = Array(rows).fill(null).map(() =>
            Array(cols).fill(null).map(() => ({ raw: '', computed: '' }))
        );

        if (INITIAL_DATA.length > 0) {
            const headers = Object.keys(INITIAL_DATA[0]);
            // headers.forEach((h, c) => { newGrid[0][c].raw = h; });
            INITIAL_DATA.forEach((row, r) => {
                headers.forEach((h, c) => {
                    newGrid[r][c].raw = row[h] !== undefined ? String(row[h]) : '';
                });
            });
        }
        return newGrid;
    };

    const [grid, setGrid] = useState(generateInitialGrid());
    const [activeCell, setActiveCell] = useState({ r: 0, c: 0 });
    const [selection, setSelection] = useState({ start: { r: 0, c: 0 }, end: { r: 0, c: 0 } });
    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [isDragging, setIsDragging] = useState(false);

    // MUI Context Menu state
    const [contextMenu, setContextMenu] = useState(null);
    const [acceptanceMenu, setAcceptanceMenu] = useState(null);
    const containerRef = useRef(null);

    const evaluateGrid = useCallback((currentGrid) => {
        const newGrid = currentGrid.map(row => row.map(cell => ({ ...cell })));

        const getCellValue = (id, visited = new Set()) => {
            if (visited.has(id)) return '#REF!';
            const pos = parseCellId(id);
            if (!pos || pos.r >= newGrid.length || pos.c >= newGrid[0].length) return 0;

            const cell = newGrid[pos.r][pos.c];
            if (!cell.raw.toString().startsWith('=')) {
                const num = Number(cell.raw);
                return !isNaN(num) && cell.raw.trim() !== '' ? num : cell.raw;
            }
            return evaluateFormula(cell.raw, new Set([...visited, id]));
        };

        const evaluateFormula = (raw, visited) => {
            let expr = raw.substring(1).toUpperCase();
            expr = expr.replace(/(SUM|AVERAGE)\(([A-Z]+[0-9]+):([A-Z]+[0-9]+)\)/g, (_, func, start, end) => {
                const cells = getRangeCells(start, end);
                const sum = cells.reduce((acc, id) => acc + (Number(getCellValue(id, visited)) || 0), 0);
                if (func === 'SUM') return sum;
                if (func === 'AVERAGE') return cells.length ? sum / cells.length : 0;
                return 0;
            });

            expr = expr.replace(/[A-Z]+[0-9]+/g, (match) => {
                const val = getCellValue(match, visited);
                return isNaN(Number(val)) || val === '' ? `"${val}"` : Number(val);
            });

            try {
                // eslint-disable-next-line no-new-func
                const result = new Function(`return ${expr}`)();
                return (result === Infinity || Number.isNaN(result)) ? '#DIV/0!' : result;
            } catch (e) {
                return '#ERROR!';
            }
        };

        let hasChanges = false;
        for (let r = 0; r < newGrid.length; r++) {
            for (let c = 0; c < newGrid[0].length; c++) {
                let computed = newGrid[r][c].raw;
                if (newGrid[r][c].raw.toString().startsWith('=')) {
                    computed = evaluateFormula(newGrid[r][c].raw, new Set([getCellId(r, c)]));
                }
                if (newGrid[r][c].computed !== computed) {
                    newGrid[r][c].computed = computed;
                    hasChanges = true;
                }
            }
        }
        return hasChanges ? newGrid : currentGrid;
    }, []);

    useEffect(() => {
        setGrid(prev => evaluateGrid(prev));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const insertRowBelow = () => {
        const r = Math.max(selection.start.r, selection.end.r);
        setGrid(prev => {
            const newGrid = [...prev];
            newGrid.splice(r + 1, 0, Array(newGrid[0].length).fill({ raw: '', computed: '' }));
            return evaluateGrid(newGrid);
        });
        handleCloseMenu();
    };

    const insertColRight = () => {
        const c = Math.max(selection.start.c, selection.end.c);
        setGrid(prev => {
            const newGrid = prev.map(row => {
                const newRow = [...row];
                newRow.splice(c + 1, 0, { raw: '', computed: '' });
                return newRow;
            });
            return evaluateGrid(newGrid);
        });
        handleCloseMenu();
    };

    const deleteCurrentRow = () => {
        const r = activeCell.r;
        if (grid[r]?.some((_, c) => isReadOnlyCell(r, c))) {
            handleCloseMenu();
            return;
        }
        if (grid.length <= 1) return;
        setGrid(prev => {
            const newGrid = [...prev];
            newGrid.splice(r, 1);
            return evaluateGrid(newGrid);
        });
        setActiveCell(prev => ({ ...prev, r: Math.min(prev.r, grid.length - 2) }));
        setSelection(prev => ({
            start: { r: Math.min(prev.start.r, grid.length - 2), c: prev.start.c },
            end: { r: Math.min(prev.end.r, grid.length - 2), c: prev.end.c }
        }));
        handleCloseMenu();
    };

    const deleteCurrentCol = () => {
        const c = activeCell.c;
        if (grid.some((_, r) => isReadOnlyCell(r, c))) {
            handleCloseMenu();
            return;
        }
        if (grid[0].length <= 1) return;
        setGrid(prev => {
            const newGrid = prev.map(row => {
                const newRow = [...row];
                newRow.splice(c, 1);
                return newRow;
            });
            return evaluateGrid(newGrid);
        });
        setActiveCell(prev => ({ ...prev, c: Math.min(prev.c, grid[0].length - 2) }));
        setSelection(prev => ({
            start: { r: prev.start.r, c: Math.min(prev.start.c, grid[0].length - 2) },
            end: { r: prev.end.r, c: Math.min(prev.end.c, grid[0].length - 2) }
        }));
        handleCloseMenu();
    };

    const saveCellEdit = useCallback(() => {
        if (!editingCell) return;
        if (isReadOnlyCell(editingCell.r, editingCell.c)) {
            setEditingCell(null);
            return;
        }
        setGrid(prev => {
            const newGrid = [...prev];
            newGrid[editingCell.r] = [...newGrid[editingCell.r]];
            newGrid[editingCell.r][editingCell.c] = {
                ...newGrid[editingCell.r][editingCell.c],
                raw: editValue
            };
            return evaluateGrid(newGrid);
        });
        setEditingCell(null);
    }, [editingCell, editValue, evaluateGrid]);

    const moveActiveCell = useCallback((dr, dc, isShiftPressed) => {
        setActiveCell(prev => {
            let r = prev.r + dr;
            let c = prev.c + dc;
            let expanded = false;

            setGrid(currentGrid => {
                let newGrid = [...currentGrid];
                if (r >= newGrid.length) {
                    newGrid.push(Array(newGrid[0].length).fill({ raw: '', computed: '' }));
                    expanded = true;
                } else if (r < 0) r = 0;

                if (c >= newGrid[0].length) {
                    newGrid = newGrid.map(row => [...row, { raw: '', computed: '' }]);
                    expanded = true;
                } else if (c < 0) c = 0;

                return expanded ? evaluateGrid(newGrid) : currentGrid;
            });

            const nextCell = { r: Math.max(0, r), c: Math.max(0, c) };
            setSelection(prevSel => ({
                start: isShiftPressed ? prevSel.start : nextCell,
                end: nextCell
            }));
            return nextCell;
        });
    }, [evaluateGrid]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!editingCell && e.target instanceof HTMLElement && e.target.closest('input, textarea, [contenteditable="true"]')) return;

            if (editingCell) {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    saveCellEdit();
                    moveActiveCell(1, 0, false);
                } else if (e.key === 'Tab') {
                    e.preventDefault();
                    saveCellEdit();
                    moveActiveCell(0, 1, false);
                } else if (e.key === 'Escape') {
                    setEditingCell(null);
                }
                return;
            }

            let handled = true;
            if (e.key === 'ArrowUp') moveActiveCell(-1, 0, e.shiftKey);
            else if (e.key === 'ArrowDown') moveActiveCell(1, 0, e.shiftKey);
            else if (e.key === 'ArrowLeft') moveActiveCell(0, -1, e.shiftKey);
            else if (e.key === 'ArrowRight') moveActiveCell(0, 1, e.shiftKey);
            else if (e.key === 'Tab') moveActiveCell(0, e.shiftKey ? -1 : 1, false);
            else if (e.key === 'Enter') {
                if (!isReadOnlyCell(activeCell.r, activeCell.c)) {
                    setEditingCell(activeCell);
                    setEditValue(grid[activeCell.r][activeCell.c].raw);
                }
            } else if (e.key === 'Backspace' || e.key === 'Delete') {
                setGrid(prev => {
                    const newGrid = [...prev];
                    const rMin = Math.min(selection.start.r, selection.end.r);
                    const rMax = Math.max(selection.start.r, selection.end.r);
                    const cMin = Math.min(selection.start.c, selection.end.c);
                    const cMax = Math.max(selection.start.c, selection.end.c);
                    for (let r = rMin; r <= rMax; r++) {
                        newGrid[r] = [...newGrid[r]];
                        for (let c = cMin; c <= cMax; c++) {
                            if (!isReadOnlyCell(r, c)) newGrid[r][c] = { raw: '', computed: '' };
                        }
                    }
                    return evaluateGrid(newGrid);
                });
            } else {
                handled = false;
            }

            if (handled) {
                e.preventDefault();
            } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                if (!isReadOnlyCell(activeCell.r, activeCell.c)) {
                    setEditingCell(activeCell);
                    setEditValue(e.key);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeCell, editingCell, grid, selection, moveActiveCell, evaluateGrid, saveCellEdit]);

    useEffect(() => {
        const handleCopy = (e) => {
            if (editingCell) return;
            e.preventDefault();
            const rMin = Math.min(selection.start.r, selection.end.r);
            const rMax = Math.max(selection.start.r, selection.end.r);
            const cMin = Math.min(selection.start.c, selection.end.c);
            const cMax = Math.max(selection.start.c, selection.end.c);

            let tsv = '';
            for (let r = rMin; r <= rMax; r++) {
                const rowData = [];
                for (let c = cMin; c <= cMax; c++) rowData.push(grid[r][c].raw);
                tsv += rowData.join('\t') + (r === rMax ? '' : '\n');
            }
            e.clipboardData.setData('text/plain', tsv);
        };

        const handlePaste = (e) => {
            if (editingCell) return;
            e.preventDefault();
            const text = e.clipboardData.getData('text/plain');
            if (!text) return;

            const rows = text.split(/\r?\n/).map(row => row.split('\t'));
            setGrid(prev => {
                let newGrid = [...prev];
                const startR = activeCell.r;
                const startC = activeCell.c;

                const requiredRows = startR + rows.length;
                const requiredCols = startC + Math.max(...rows.map(r => r.length));

                while (newGrid.length < requiredRows) {
                    newGrid.push(Array(newGrid[0].length).fill({ raw: '', computed: '' }));
                }
                if (newGrid[0].length < requiredCols) {
                    newGrid = newGrid.map(row => {
                        const newRow = [...row];
                        while (newRow.length < requiredCols) newRow.push({ raw: '', computed: '' });
                        return newRow;
                    });
                }

                rows.forEach((rowData, i) => {
                    rowData.forEach((cellData, j) => {
                        if (isReadOnlyCell(startR + i, startC + j)) return;
                        newGrid[startR + i] = [...newGrid[startR + i]];
                        newGrid[startR + i][startC + j] = { raw: cellData, computed: '' };
                    });
                });
                return evaluateGrid(newGrid);
            });
        };

        document.addEventListener('copy', handleCopy);
        document.addEventListener('paste', handlePaste);
        return () => {
            document.removeEventListener('copy', handleCopy);
            document.removeEventListener('paste', handlePaste);
        };
    }, [grid, selection, activeCell, editingCell, evaluateGrid]);

    const handleMouseDown = (e, r, c) => {
        if (e.button === 2) return; // Ignore right click
        if (editingCell && editValue.startsWith('=')) {
            e.preventDefault();
            if (editingCell.r === r && editingCell.c === c) return;
            setEditValue(prev => prev + getCellId(r, c));
            return;
        }
        if (editingCell) saveCellEdit();
        setActiveCell({ r, c });
        setSelection({ start: { r, c }, end: { r, c } });
        if (ACCEPTANCE_COLUMN[getColLetter(c)]) {
            setIsDragging(false);
            handleCloseMenu();
            setAcceptanceMenu({ anchorEl: e.currentTarget, r, c });
            return;
        }
        setIsDragging(true);
        handleCloseMenu();
    };

    const setAcceptanceValue = (value) => {
        if (!acceptanceMenu) return;
        const { r, c } = acceptanceMenu;
        setGrid(prev => {
            const newGrid = [...prev];
            newGrid[r] = [...newGrid[r]];
            newGrid[r][c] = { ...newGrid[r][c], raw: value };
            return evaluateGrid(newGrid);
        });
        setAcceptanceMenu(null);
    };

    const handleMouseEnter = (r, c) => {
        if (isDragging && !editingCell) {
            setSelection(prev => ({ ...prev, end: { r, c } }));
        }
    };

    const handleContextMenu = (e, r, c) => {
        e.preventDefault();
        if (r < Math.min(selection.start.r, selection.end.r) || r > Math.max(selection.start.r, selection.end.r) ||
            c < Math.min(selection.start.c, selection.end.c) || c > Math.max(selection.start.c, selection.end.c)) {
            setActiveCell({ r, c });
            setSelection({ start: { r, c }, end: { r, c } });
        }
        setContextMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4 });
    };

    const handleCloseMenu = () => setContextMenu(null);

    useEffect(() => {
        const handleMouseUp = () => setIsDragging(false);
        window.addEventListener('mouseup', handleMouseUp);
        return () => window.removeEventListener('mouseup', handleMouseUp);
    }, []);

    const rMin = Math.min(selection.start.r, selection.end.r);
    const rMax = Math.max(selection.start.r, selection.end.r);
    const cMin = Math.min(selection.start.c, selection.end.c);
    const cMax = Math.max(selection.start.c, selection.end.c);

    const downloadJson = () => {
        const rows = grid.map((row, r) => row.map((cell, c) => (
            editingCell?.r === r && editingCell?.c === c ? editValue : cell.raw
        )));
        const lastRow = rows.findLastIndex(row => row.some(value => value !== ''));
        const lastColumn = rows.reduce((lastIndex, row) => Math.max(
            lastIndex,
            row.findLastIndex(value => value !== '')
        ), -1);
        const data = rows.slice(0, lastRow + 1).map(row => row.slice(0, lastColumn + 1));
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'spax-excel-data.json';
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#f9f9f9', overflow: 'hidden' }}>

            {/* MUI Top Toolbar */}
            <AppBar position="static" color="inherit" elevation={1} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <Toolbar variant="dense" sx={{ minHeight: 48, px: 2, borderBottom: '1px solid #e0e0e0', gap: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        Spax Excel
                    </Typography>
                    <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ width: 40, textAlign: 'center', fontWeight: 'bold' }}>
                        {getCellId(activeCell.r, activeCell.c)}
                    </Typography>
                    <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 1 }} />
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mr: 1 }}>
                        fx
                    </Typography>
                    <InputBase
                        sx={{ flex: 1, ml: 1, fontSize: '0.875rem', fontFamily: 'monospace' }}
                        value={editingCell ? editValue : grid[activeCell.r]?.[activeCell.c]?.raw || ''}
                        onChange={(e) => {
                            const targetCell = editingCell || activeCell;
                            if (isReadOnlyCell(targetCell.r, targetCell.c)) return;
                            if (!editingCell) setEditingCell(activeCell);
                            setEditValue(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                if (editingCell) { saveCellEdit(); moveActiveCell(1, 0, false); }
                                e.target.blur();
                            }
                        }}
                        placeholder="Select a cell to enter a value or formula"
                    />
                    <Tooltip title="Download JSON">
                        <IconButton aria-label="Download sheet as JSON" onClick={downloadJson} size="small">
                            <DownloadOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </AppBar>

            {/* Grid Canvas */}
            <Box sx={{ flex: 1, overflow: 'auto', position: 'relative' }} ref={containerRef}>
                <TableContainer sx={{ maxHeight: '100%' }}>
                    <Table stickyHeader size="small" sx={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: 'max-content' }}>

                        {/* Table Header */}
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        position: 'sticky', left: 0, zIndex: 3,
                                        backgroundColor: '#f5f5f5', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0',
                                        width: 40, p: 0, minWidth: 40
                                    }}
                                />
                                {grid[0].map((_, c) => (
                                    <TableCell
                                        key={c}
                                        align="center"
                                        sx={{
                                            backgroundColor: c >= cMin && c <= cMax ? '#e3f2fd' : '#f5f5f5',
                                            color: c >= cMin && c <= cMax ? '#1976d2' : 'text.secondary',
                                            borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0',
                                            p: 0, minWidth: 100, height: 28, userSelect: 'none', fontWeight: 'bold', fontSize: '0.75rem',
                                            zIndex: 2
                                        }}
                                    >
                                        {getColLetter(c)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        {/* Table Body */}
                        <TableBody>
                            {grid.map((row, r) => (
                                <TableRow key={r} hover={false}>
                                    {/* Row Header */}
                                    <TableCell
                                        align="center"
                                        sx={{
                                            position: 'sticky', left: 0, zIndex: 1,
                                            backgroundColor: r >= rMin && r <= rMax ? '#e3f2fd' : '#f5f5f5',
                                            color: r >= rMin && r <= rMax ? '#1976d2' : 'text.secondary',
                                            borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0',
                                            p: 0, height: 28, userSelect: 'none', fontWeight: 'bold', fontSize: '0.75rem'
                                        }}
                                    >
                                        {r + 1}
                                    </TableCell>

                                    {/* Data Cells */}
                                    {row.map((cell, c) => {
                                        const isActive = activeCell.r === r && activeCell.c === c;
                                        const isEditing = editingCell?.r === r && editingCell?.c === c;
                                        const inSelection = r >= rMin && r <= rMax && c >= cMin && c <= cMax;
                                        const alignRight = isNumericOrDate(cell.computed);

                                        return (
                                            <TableCell
                                                key={c}
                                                onMouseDown={(e) => handleMouseDown(e, r, c)}
                                                onMouseEnter={() => handleMouseEnter(r, c)}
                                                onContextMenu={(e) => handleContextMenu(e, r, c)}
                                                onDoubleClick={() => {
                                                    if (isReadOnlyCell(r, c)) return;
                                                    setEditingCell({ r, c });
                                                    setEditValue(cell.raw);
                                                }}
                                                sx={{
                                                    position: 'relative',
                                                    p: 0, height: 28, minWidth: 100, px:0.5,
                                                    borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0',
                                                    backgroundColor: inSelection && !isActive && !isEditing ? 'rgba(25, 118, 210, 0.08)' : 'white',
                                                    cursor: 'cell',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'visible', // Keep visible for absolute positioning outlines
                                                }}
                                            >
                                                {inSelection && !isEditing && (
                                                    <Box sx={{
                                                        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: isActive ? 2 : 1,
                                                        borderTop: inSelection && r === rMin ? '2px solid #1976d2' : 'none',
                                                        borderBottom: inSelection && r === rMax ? '2px solid #1976d2' : 'none',
                                                        borderLeft: inSelection && c === cMin ? '2px solid #1976d2' : 'none',
                                                        borderRight: inSelection && c === cMax ? '2px solid #1976d2' : 'none',
                                                    }}>
                                                        {isActive && <Box sx={{ position: 'absolute', inset: 0, border: '2px solid #1976d2' }} />}
                                                        {inSelection && r === rMax && c === cMax && isActive && (
                                                            <Box sx={{ position: 'absolute', bottom: -3, right: -3, width: 6, height: 6, backgroundColor: '#1976d2', border: '1px solid white', cursor: 'crosshair' }} />
                                                        )}
                                                    </Box>
                                                )}

                                                {isEditing ? (
                                                    <InputBase
                                                        autoFocus
                                                        value={editValue}
                                                        onChange={(e) => {
                                                            if (!isReadOnlyCell(r, c)) setEditValue(e.target.value);
                                                        }}
                                                        onBlur={saveCellEdit}
                                                        sx={{
                                                            position: 'absolute', inset: 0, px: 1,
                                                            backgroundColor: 'white', zIndex: 10,
                                                            border: '2px solid #1976d2', boxShadow: 2,
                                                            '& input': { textAlign: alignRight ? 'right' : 'left', fontSize: '0.875rem', p: 0, height: '100%' }
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            width: '100%', height: '100%', px: 0,
                                                            display: 'flex', alignItems: 'center',
                                                            justifyContent: alignRight ? 'flex-end' : 'flex-start',
                                                            pointerEvents: 'none', userSelect: 'none',
                                                            color: ['#ERROR!', '#REF!', '#DIV/0!'].includes(cell.computed) ? 'error.main' : 'text.primary',
                                                            fontWeight: ['#ERROR!', '#REF!', '#DIV/0!'].includes(cell.computed) ? 'bold' : 'normal'
                                                        }}
                                                    >
                                                        {cell.computed}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* MUI Context Menu */}
            <Menu
                open={contextMenu !== null}
                onClose={handleCloseMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
                }
                PaperProps={{ sx: { minWidth: 160 } }}
            >
                <MenuItem onClick={insertRowBelow}>
                    <ListItemText primary="Insert Row Below" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </MenuItem>
                <MenuItem onClick={insertColRight}>
                    <ListItemText primary="Insert Column Right" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </MenuItem>
                <Divider />
                <MenuItem onClick={deleteCurrentRow} sx={{ color: 'error.main' }}>
                    <ListItemText primary="Delete Row" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </MenuItem>
                <MenuItem onClick={deleteCurrentCol} sx={{ color: 'error.main' }}>
                    <ListItemText primary="Delete Column" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </MenuItem>
            </Menu>
            <Menu
                open={acceptanceMenu !== null}
                onClose={() => setAcceptanceMenu(null)}
                anchorEl={acceptanceMenu?.anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { minWidth: 160 } }}
            >
                {(ACCEPTANCE_COLUMN[getColLetter(acceptanceMenu?.c)] || []).map((value) => (
                    <MenuItem key={value} onClick={() => setAcceptanceValue(value)} sx={{ zoom: 0.75, minWidth: 150 }}>
                        <ListItemText primary={value} primaryTypographyProps={{ fontSize: '0.25rem' }} />
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
}
